/* **The split dialog** — one fixed window, a form column, and a live panel beside it.
 *
 * A stylesheet with no component, the same shape as `emberButton.module.css` and
 * `segmented.module.css`: there is no behaviour here, and the two surfaces that wear it
 * (`features/assign-plan`, `features/create-plan`) fill their two halves with entirely different
 * things. A component would be a wrapper that only forwards children.
 *
 * Canon: `docs/design/plan-propozycje-2026-09-21/index.html#d/*`, its `.dl.split` block.
 *
 * ── One size on every state, and both halves scroll ─────────────────────────────────────────
 * The window is 1180x820 whatever is in it, which is the exercise creator's D11 rule: a dialog
 * that resizes moves its primary, and the trainer is pressing it. At 1280x620 there is no 820px
 * to have, so the height is `min(…, --overlay-max-block)` and the two panes scroll inside a
 * shorter box — fit-or-scroll, never clip. `min-height: 0` on both is what makes that true: a
 * grid item's default `min-height: auto` refuses to shrink below its content and the scroller
 * silently disappears.
 *
 * ── What the SURFACE declares ───────────────────────────────────────────────────────────────
 * Everything inside the two panes, and nothing this file states. A declaration written on both
 * sides is settled by chunk order, which differs between the dev server and the build. */

.backdrop {
  position: fixed;
  inset: var(--space-0);
  background: var(--scrim);
  backdrop-filter: var(--blur-scrim);
  transition-property: opacity;
  transition-duration: var(--dur-base);
  transition-timing-function: var(--ease);
}

/* ── Both windows arrive and leave, rather than cutting in (24 September 2026) ───────────────
 * The scrim fades and the window fades — opacity only, so a window that opens in place does not
 * travel under the probes that hold its panes still while their reads land (`e2e/plan-create`'s
 * movement case). The one arrival that also RISES is the create window taking over from the
 * leftover-plan prompt, and that surface adds it itself. Both on `data-starting-style` /
 * `data-ending-style`,
 * which Base UI sets for exactly one frame either side. No media query: `tokens/base.css`
 * collapses these durations under `prefers-reduced-motion`, so a still viewer gets the window
 * fully drawn on its first frame. It was added for „Nowy program", which a leftover-plan prompt
 * now hands over to, and the assign window wears the same file, so it arrives the same way. */
.backdrop[data-starting-style],
.backdrop[data-ending-style] {
  opacity: 0;
}

.popup {
  position: fixed;
  top: 50%;
  left: 50%;
  display: grid;
  grid-template-rows: auto 1fr auto;
  grid-template-columns: var(--w-plan-dialog-form) 1fr;
  width: var(--w-plan-dialog);
  max-width: calc(100vw - var(--space-12));
  height: min(var(--h-plan-dialog), var(--overlay-max-block));
  overflow: hidden;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-modal);
  transform: translate(-50%, -50%);
  transition-property: opacity;
  transition-duration: var(--dur-base);
  transition-timing-function: var(--ease);
}

.popup[data-starting-style],
.popup[data-ending-style] {
  opacity: 0;
}

/* The dismiss cross in the head's right-hand corner, which both windows draw — promoted out of
 * `features/assign-plan` on 22 September 2026, when "Nowy program" grew the one the specimen's
 * head has always had. The drawing is `shared/ui/FormGlyphs`' `CloseGlyph`. */
.close {
  display: grid;
  flex: none;
  place-items: center;
  width: var(--hit-target);
  height: var(--hit-target);
  color: var(--text-2);
  background: transparent;
  border: 0;
  border-radius: var(--radius-8px);
  cursor: pointer;
}

.close:hover:not([aria-disabled='true']) {
  color: var(--text);
  background: var(--hover-tint);
}

/* Held — the assign dialog's unsaved send in flight. `aria-disabled` rather than the attribute, so
 * the reason stays reachable; `--text-3` is the tier authored for inert ink, as in
 * `dialogSurface.module.css`, and there is no hover to answer. */
.close[aria-disabled='true'] {
  color: var(--text-3);
  cursor: default;
}

.close svg {
  width: var(--space-14px);
  height: var(--space-14px);
}

/* The head and the foot span both columns — the echo belongs to the whole window, and so does the
 * one primary. */
.head,
.foot {
  grid-column: 1 / -1;
}

.head {
  display: flex;
  gap: var(--space-14px);
  align-items: center;
  min-height: var(--h-plan-dialog-head);
  padding: var(--space-0) var(--space-22px) var(--space-0) var(--space-26px);
  border-bottom: var(--border-hairline) solid var(--border);
}

.foot {
  display: flex;
  gap: var(--space-10px);
  align-items: center;
  min-height: var(--h-plan-dialog-foot);
  padding: var(--space-0) var(--space-22px) var(--space-0) var(--space-26px);
  border-top: var(--border-hairline) solid var(--border);
}

/* The form column. `min-height: 0` is load-bearing — see the header. */
.form {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  min-height: var(--space-0);
  padding: var(--space-26px) var(--space-30px);
  overflow: auto;
}

/* The live column: the projection, on the sunken ground the canon gives it. */
.live {
  display: flex;
  flex-direction: column;
  gap: var(--space-14px);
  min-height: var(--space-0);
  padding: var(--space-22px) var(--space-26px) var(--space-22px) var(--space-6);
  overflow: auto;
  background: var(--bg-inset);
  border-left: var(--border-hairline) solid var(--border);
}

/* The one gap either column pushes its trailing content to the bottom with. */
.spacer {
  flex: 1;
}

/* ── The head's title and the foot's row ─────────────────────────────────────────────────────
 *
 * The two surfaces wrote these five blocks byte for byte identically, which is what the styling
 * rules call the near-duplicate: the next change to the refusal's ink or the primary's radius would
 * have landed in one dialog and not the other. They are chrome, like `.head` and `.foot` above, and
 * they belong beside them. */

.title {
  margin: var(--space-0);
  font-size: var(--fs-h3);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-heading);
}

/* The head's echo beside the title — the plan being assigned, the client a plan is being built for.
 * Promoted with the title on 23 September 2026, when „Nowy plan" grew the client echo the assign
 * dialog already drew. Never declined: the name is said in the nominative, as the roster has it. */
.echo {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

/* The way out. Quiet, unfilled and unbordered — the accent is spent on the one control that writes. */
.ghost {
  padding: var(--space-0) var(--space-6px);
  font: inherit;
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  background: none;
  border: 0;
  cursor: pointer;
}

.ghost:hover:not([aria-disabled='true']) {
  color: var(--text);
}

/* Held, like `.close` above: inert ink and no hover. */
.ghost[aria-disabled='true'] {
  color: var(--text-3);
  cursor: default;
}

/* The sentence beside the primary, not only behind it. Its own element rather than a cell in the
 * foot's flex row, so a long refusal wraps beside the button instead of squeezing it. */
.reason,
.failure {
  font-size: var(--fs-note);
  line-height: var(--lh-hint);
  color: var(--text-2);
}

/* What the SERVER refused, which is a different thing from what the form is still missing. Beside
 * `.reason` in the same file rather than in a consumer's own: two single-class rules setting the
 * same property are settled by chunk order, and inside one file the order is the file's. */
.failure {
  color: var(--danger-ink);
}

/* Geometry only — the fill, the ink and the pressed state are `shared/ui/emberButton`'s, and as
 * ordinary properties rather than `--cta-*` custom properties, which the value gate cannot see. */
.primary {
  height: var(--h-ex-wizard-cta);
  padding: var(--space-0) var(--space-5);
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-heading);
  border-radius: var(--radius-12px);
}
